/**
 * ports.is_sim_converted:
 *   1 = No (not converted)
 *   2 = Yes (converted) — enough on its own; other detail fields may be empty
 *
 * ports.number_source:
 *   "gateway" | "service"
 *
 * Service number:
 *   number_source === "service"
 *   OR number_source === "gateway" AND is_sim_converted === 2
 */

export const NUMBER_SOURCE_GATEWAY = 'gateway';
export const NUMBER_SOURCE_SERVICE = 'service';

export function isSimConvertedYes(port) {
  if (!port) return false;
  const v = port.is_sim_converted;
  if (v == null || v === "") return false;
  return Number(v) === 2;
}

export function normalizeNumberSource(value) {
  const s = String(value ?? '').trim().toLowerCase();
  if (s === NUMBER_SOURCE_SERVICE || s === '2') return NUMBER_SOURCE_SERVICE;
  return NUMBER_SOURCE_GATEWAY;
}

export function isServiceNumber(port) {
  if (!port) return false;
  if (normalizeNumberSource(port.number_source) === NUMBER_SOURCE_SERVICE) return true;
  return isSimConvertedYes(port);
}

export function isGatewayProcessed(port) {
  return !isServiceNumber(port);
}

/**
 * Activate stays on a gateway number until that number is converted
 * (is_sim_converted === 2). Born service numbers never show it.
 * A different service row with the same digits does not count.
 */
export function shouldShowActivate(port) {
  if (!port) return false;
  if (isSimConvertedYes(port)) return false;
  if (normalizeNumberSource(port.number_source) === NUMBER_SOURCE_SERVICE) return false;
  return true;
}

/** Same port id from the full list, so a thin row copy still sees conversion. */
export function portRecordForActivate(port, allPorts = []) {
  if (!port) return port;
  const list = Array.isArray(allPorts) ? allPorts : [];
  const byId = list.find((p) => p && Number(p.id) === Number(port.id));
  return byId || port;
}

export function isSimConversionRequested(port) {
  if (!port) return false;
  return (
    port.req_sim_conversion === true ||
    port.req_sim_conversion === 1 ||
    port.req_sim_conversion === '1'
  );
}

export function isDeadForSimConversion(port) {
  if (!port) return false;
  if (port.is_dead === true || port.is_dead === 1 || port.is_dead === '1') return true;
  return (
    port.inactivity_window_missed === true ||
    port.inactivity_window_missed === 1 ||
    port.inactivity_window_missed === '1'
  );
}

export function canRequestSimConversion(port) {
  if (!port || port.archived) return false;
  if (isDeadForSimConversion(port)) return false;
  if (!String(port.assigned_number || '').trim()) return false;
  if (isSimConversionRequested(port)) return false;
  if (isServiceNumber(port)) return false;
  return true;
}

/**
 * Buyer numbers filter:
 *   "" | "converted" | "not_converted" | "requested"
 */
export function matchesConversionStatusFilter(port, filter) {
  if (!filter) return true;
  if (!port) return false;
  if (filter === 'converted') return isSimConvertedYes(port);
  if (filter === 'requested') {
    return isSimConversionRequested(port) && !isSimConvertedYes(port);
  }
  if (filter === 'not_converted') return !isServiceNumber(port);
  if (filter === 'new_tt_email') return false;
  return true;
}

function assignedDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

/**
 * Map of assigned-number digits -> live ports.
 * Build once per ports list; pass into collectPortsForAdviceRow.
 */
export function indexPortsByAssignedDigits(allPorts = []) {
  const map = new Map();
  for (const p of allPorts || []) {
    if (!p || p.archived) continue;
    const digits = assignedDigits(p.assigned_number);
    if (!digits) continue;
    if (!map.has(digits)) map.set(digits, []);
    map.get(digits).push(p);
  }
  return map;
}

function portsByDigitsIndex(allPortsOrIndex) {
  if (allPortsOrIndex instanceof Map) return allPortsOrIndex;
  return indexPortsByAssignedDigits(allPortsOrIndex);
}

export function collectPortsForAdviceRow(row, portsByEmail, allPortsOrIndex = []) {
  const email = (row?.email || '').trim().toLowerCase();
  const linked =
    (portsByEmail instanceof Map ? portsByEmail.get(email) : null) || [];
  if (linked.length) return linked.filter(Boolean);

  const managed = Array.isArray(row?.user_managed_numbers)
    ? row.user_managed_numbers
    : [];
  if (!managed.length) return [];

  const byDigits = portsByDigitsIndex(allPortsOrIndex);
  const seen = new Set();
  const out = [];
  for (const n of managed) {
    const digits = assignedDigits(n);
    if (!digits) continue;
    for (const p of byDigits.get(digits) || []) {
      const key = p.id != null ? p.id : p;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(p);
    }
  }
  return out;
}

export function adviceRowMatchesConversionFilter(
  row,
  portsByEmail,
  allPortsOrIndex,
  filter
) {
  if (!filter) return true;
  const ports = collectPortsForAdviceRow(row, portsByEmail, allPortsOrIndex);
  return ports.some((p) => matchesConversionStatusFilter(p, filter));
}

export function serviceBadgeLabel(port) {
  if (isSimConvertedYes(port)) return 'Converted';
  if (isServiceNumber(port)) return 'Service';
  return null;
}

/** Prefer a port record that carries is_sim_converted when the row copy is incomplete. */
export function resolvePortForConversion(port, allPorts = []) {
  if (!port) return port;
  if (isServiceNumber(port)) return port;

  const list = Array.isArray(allPorts) ? allPorts : [];
  const byId = list.find((p) => p && Number(p.id) === Number(port.id));
  if (byId && isServiceNumber(byId)) return byId;

  const digits = String(port.assigned_number || "").replace(/\D/g, "");
  if (digits) {
    const byNum = list.find(
      (p) =>
        p &&
        String(p.assigned_number || "").replace(/\D/g, "") === digits &&
        isServiceNumber(p)
    );
    if (byNum) return byNum;
  }

  return byId || port;
}
